/**
 * Per-level drone difficulty.
 *
 * Difficulty is a property of the *level*, not a global setting: a quiet
 * tutorial deck and a lethal gauntlet can sit in the same campaign. The tier
 * changes how hard the drones hit back — more shots, longer bursts, less rest,
 * a faster wake-up, a better lead — and how their fire is spread.
 *
 * **The spread tightens as a burst fires.** The first shot of a burst is the
 * loosest (`jitterStart`) and the last is the tightest (`jitterEnd`), so a
 * burst reads as "spray, then settle onto you". `jitterChaos` scrambles *where*
 * in that ramp each shot lands: at 0 the burst always sweeps wide to tight, and
 * at 1 every shot takes a random point on the ramp, so the pattern cannot be
 * memorised for a bullet-time lean. Chaos also adds a small vertical scatter.
 *
 * These numbers live here rather than in `balance.ts` because they are a table
 * of whole profiles, not individual tunables; the numbers that do not vary by
 * tier (bolt speed and damage, engage range and delay, hover and patrol) stay
 * in `BALANCE.combat`.
 *
 * The fairness floor is that a *pack* of four drones engaging at once still
 * leaves one bullet-time window to react in, even at `lethal` (`T-INT-27`).
 * That is a floor, not a guarantee: a deck that puts more than four drones on
 * one sight line is deadlier on purpose, and the showcase decks are where that
 * is measured.
 */
export type DifficultyTier = 'calm' | 'standard' | 'hard' | 'lethal';

export interface DifficultyProfile {
  /** Shots fired in one burst. */
  burstCount: number;
  /** Time between shots inside a burst, seconds. */
  burstInterval: number;
  /** Quiet time between bursts, seconds. */
  burstRest: number;
  /** Reaction time after first acquiring the target, seconds. */
  reactTime: number;
  /** Extra pause after line of sight is lost and then regained, seconds. */
  reacquireDelay: number;
  /** How much of the target's velocity the shot leads, 0 to 1. */
  leadFactor: number;
  /** Spread on the first shot of a burst, radians. */
  jitterStart: number;
  /** Spread on the last shot of a burst: the burst tightens as it fires. */
  jitterEnd: number;
  /**
   * 0 to 1: how unpredictable the spread is. Above zero a shot takes a random
   * point on the tighten ramp instead of marching along it, and a vertical
   * scatter is mixed in.
   */
  jitterChaos: number;
}

/** Ordered from the gentlest to the most punishing. */
export const DIFFICULTY_TIERS: readonly DifficultyTier[] = ['calm', 'standard', 'hard', 'lethal'];

export const DIFFICULTY_PROFILES: Record<DifficultyTier, DifficultyProfile> = {
  calm: {
    burstCount: 3,
    burstInterval: 0.22,
    burstRest: 6.5,
    reactTime: 0.6,
    reacquireDelay: 0.8,
    leadFactor: 0.6,
    jitterStart: 0.14,
    jitterEnd: 0.06,
    jitterChaos: 0,
  },
  standard: {
    burstCount: 4,
    burstInterval: 0.18,
    burstRest: 4.5,
    reactTime: 0.45,
    reacquireDelay: 0.5,
    leadFactor: 0.8,
    jitterStart: 0.12,
    jitterEnd: 0.03,
    jitterChaos: 0,
  },
  hard: {
    burstCount: 6,
    burstInterval: 0.15,
    burstRest: 3,
    reactTime: 0.3,
    reacquireDelay: 0.3,
    leadFactor: 0.9,
    jitterStart: 0.05,
    jitterEnd: 0,
    jitterChaos: 0.6,
  },
  lethal: {
    burstCount: 8,
    // Deliberately a long burst with a *short* rest rather than the fastest
    // interval: eight shots is already 64 potential damage, and the guardrail
    // is that even the worst case leaves one bullet-time window to react in
    // (see the difficulty tests).
    burstInterval: 0.14,
    burstRest: 3,
    reactTime: 0.15,
    reacquireDelay: 0.1,
    leadFactor: 1,
    jitterStart: 0.02,
    jitterEnd: 0,
    jitterChaos: 1,
  },
};

/** The tier a level with no tag runs at. */
export const DEFAULT_DIFFICULTY: DifficultyTier = 'standard';

export function isDifficultyTier(value: unknown): value is DifficultyTier {
  return typeof value === 'string' && (DIFFICULTY_TIERS as readonly string[]).includes(value);
}

export function difficultyProfile(tier: DifficultyTier | undefined): DifficultyProfile {
  return DIFFICULTY_PROFILES[tier ?? DEFAULT_DIFFICULTY];
}

/**
 * The spread for one shot of a burst, radians.
 *
 * `index` is the shot's position in the burst (0 is the first) and `roll` is a
 * deterministic random in [0, 1) that only matters once a profile carries
 * chaos: at chaos 1 every shot is a random point on the ramp rather than the
 * next step along it.
 */
export function burstSpread(
  profile: DifficultyProfile,
  index: number,
  count: number,
  roll = 0,
): number {
  if (count <= 1) return profile.jitterEnd;
  const linear = Math.min(1, Math.max(0, index / (count - 1)));
  const t = linear * (1 - profile.jitterChaos) + roll * profile.jitterChaos;
  return profile.jitterStart + (profile.jitterEnd - profile.jitterStart) * t;
}
